import { useContext, useMemo, useState } from "react";
import Modal from "~/components/Modal";
import FormGroup from "~/components/FormGroup";
import { Button } from "~/components/ui/button";
import { Spinner } from "~/components/ui/spinner";
import styles from "./styles.module.scss";
import {
  docRelationTypeString,
  DocumentRelationType,
  DocRelation,
  Document,
  DocumentRelationTypeId,
  DocumentSearch,
} from "~/document";
import cn from "classnames";
import DocumentSelect from "~/components/DocumentSelect";
import DocumentRelationSelect from "~/components/DocumentRelationSelect";
import paths from "~/paths";
import { NavLink } from "react-router-dom";
import { observer } from "mobx-react";
import { ToastNotificationContext } from "~/components/ToastNotificationContext";
import docStore from "~/stores/document";
import { consumeLastApiErrorDetails } from "~/api";

interface LinkDocModalProps {
  doc: Document;
  close: () => void;
  open: boolean;
  presentRelations: Set<DocumentRelationTypeId>;
  onChanged: () => void;
}

const LinkDocModal = (props: LinkDocModalProps) => {
  const [selectedDoc, setSelectedDoc] = useState<DocumentSearch | null>(null);
  const [type, setType] = useState<DocumentRelationType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { setNotificationData, showNotificationWithTimeout } = useContext(ToastNotificationContext);
  const canSubmit = type !== null && selectedDoc !== null;

  const submit = async () => {
    setSubmitting(true);
    const res = await docStore.addRelatedDoc(props.doc, type!.id, selectedDoc!);
    setSubmitting(false);
    if (res === null) {
      setNotificationData({
        kind: "error",
        title: "Error",
        subtitle: "Document relation couldn't be added",
        details: consumeLastApiErrorDetails(),
      });
      showNotificationWithTimeout();
      return;
    }
    props.onChanged();
    setSelectedDoc(null);
    setType(null);
    props.close();
    setNotificationData({ kind: "success", title: "Success", subtitle: "Document relation added successfully" });
    showNotificationWithTimeout();
  };

  return (
    <Modal
      open={props.open}
      className="overflow-visible"
      modalHeading="Add relation"
      modalLabel="Document relations"
      onRequestClose={props.close}
      onRequestSubmit={submit}
      primaryButtonDisabled={!canSubmit || submitting}
      primaryButtonText={submitting ? "Adding..." : "Confirm"}
      secondaryButtonText="Cancel"
    >
      <div className={styles.linkRelDocModal}>
        <FormGroup legendText="Select document">
          <DocumentSelect
            onChange={(d: DocumentSearch[]) => setSelectedDoc(d[0])}
            idKey="dbid"
            itemToString={(doc) => `${doc.title} - ${doc.protocol}`}
            selectProps={{
              isMulti: false,
              filterOption: (option: any, input: string) => {
                const label = option?.label ? option.label.toLowerCase().trim() : "";
                const protocol = option?.data?.protocol ? option.data.protocol.toLowerCase().trim() : "";
                return label.includes(input.toLowerCase()) || protocol.includes(input.toLowerCase());
              },
            }}
            value={selectedDoc ? [selectedDoc.dbid] : []}
          />
        </FormGroup>
        <FormGroup legendText="Relation type">
          <DocumentRelationSelect
            filter={(item) => {
              return !props.presentRelations.has(item.id);
            }}
            onChange={(d) => setType(d[0])}
            selectProps={{ isMulti: false }}
            value={type ? [type] : []}
          />
        </FormGroup>
      </div>
    </Modal>
  );
};

interface RelatedDocumentsSelectProps {
  doc: Document;
  docs: DocRelation[];
  showModal: boolean;
  setShowModal: (v: boolean) => void;
  onChanged: () => void;
}

const RelatedDocument = observer(
  ({ item, doc, onChanged }: { item: DocRelation; doc: Document; onChanged: () => void }) => {
    const [loading, setLoading] = useState(false);
    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
    const { setNotificationData, showNotificationWithTimeout } = useContext(ToastNotificationContext);

    const remove = async () => {
      setLoading(true);
      setConfirmationModalVisible(false);
      const success = await docStore.removeRelatedDoc(doc.protocol, doc.id, item.other.id, item.type);
      setLoading(false);
      if (success) {
        onChanged();
        setNotificationData({ kind: "success", title: "Success", subtitle: "Document relation removed" });
      } else {
        setNotificationData({
          kind: "error",
          title: "Error",
          subtitle: "Document relation couldn't be removed",
          details: consumeLastApiErrorDetails(),
        });
      }
      showNotificationWithTimeout();
    };

    return (
      <div className={cn(["flex flex-row justify-between w-full", styles.listItem])}>
        <div className="w-full">
          <div>
            <NavLink className="text-primary-600 hover:underline" to={paths.construct.docDetail(item.other.protocol)}>
              {item.other.title}
            </NavLink>
            <div className="text-xs [color:var(--text-02)]">{docRelationTypeString(item)}</div>
          </div>
        </div>
        <div className="flex flex-row items-start">
          {loading && <Spinner className="mr-2 size-4" />}
          <Button
            onClick={() => setConfirmationModalVisible(true)}
            disabled={loading}
            size="sm"
            variant="destructive"
            className="text-xs"
            title="Remove Language Relation"
          >
            Remove
          </Button>
        </div>
        <Modal
          open={confirmationModalVisible}
          modalHeading="Are you sure?"
          onRequestSubmit={remove}
          onRequestClose={() => setConfirmationModalVisible(false)}
          onSecondarySubmit={() => setConfirmationModalVisible(false)}
          secondaryButtonText="Cancel"
          primaryButtonText="I am sure"
        >
          <div>You are about to remove a relation.</div>
          <div className="font-bold">Do you want to continue?</div>
        </Modal>
      </div>
    );
  }
);

const RelatedDocumentsSelect = observer((props: RelatedDocumentsSelectProps) => {
  const { docs, onChanged } = props;
  const showLinkDocModal = props.showModal;
  const setShowLinkDocModal = props.setShowModal;

  const renderedVersions = useMemo(() => {
    if (docs.length === 0) return <div className="[color:var(--text-02)] text-sm">No related documents</div>;
    return docs.map((item) => (
      <RelatedDocument
        item={item}
        key={`related-doc-${item.id || item.other.id}`}
        doc={props.doc}
        onChanged={onChanged}
      />
    ));
  }, [docs, props.doc, onChanged]);

  const presentRelations = useMemo(() => new Set([...docs.map((a) => a.id!)]), [docs]);

  return (
    <div>
      <div className="flex justify-between flex-row items-center">
        <div className="text-sm">
          <strong>Related documents</strong>
        </div>
        <Button variant="default" onClick={() => setShowLinkDocModal(true)} size="sm" className="text-xs">
          Link related document
        </Button>
      </div>
      <div className={styles.relDocsList}>{renderedVersions}</div>
      <LinkDocModal
        open={showLinkDocModal}
        doc={props.doc}
        presentRelations={presentRelations}
        onChanged={onChanged}
        close={() => setShowLinkDocModal(false)}
      />
    </div>
  );
});

export default RelatedDocumentsSelect;
